import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TreatmentPlanAppointment } from "../../../../services/treatmentPlanService";
import {
  EditNoteIcon,
  MoveAppointmentIcon,
  TrashIcon,
} from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import { deduplicateCdtCodesStructured } from "../../../../utils/cdtCodeUtils";
import { TreatmentStepChips, TreatmentStepEmptyValue } from "../TreatmentStepChips";
import {
  formatFeeWithoutCents,
  getAppointmentDuration,
  getAppointmentFee,
  getOptionLabel,
  getProcedureFee,
} from "../../../../utils/appointmentDisplayUtils";
import {
  hasMissingSurface,
  hasMissingTooth,
  parseToothSurfaces,
} from "../../../../utils/toothSurfacesUtils";
import { aggregateTreatAreaCapabilities } from "../../../../utils/treatAreaCapabilities";
import {
  AppointmentContainer,
  AppointmentHeader,
  AppointmentHeaderMainCell,
  AppointmentHeaderTitleRow,
  AppointmentHeaderNumber,
  AppointmentHeaderName,
  AppointmentHeaderBadge,
  AppointmentHeaderDuration,
  AppointmentHeaderFeeCell,
  AppointmentFee,
  AppointmentActions,
  ActionIconButton,
  DragHandle,
  AppointmentFieldLabel,
  ProcedureCard,
  ProcedureTitle,
  ProcedureMobileField,
  ProcedureFieldLabel,
  ProcedureCdtValue,
  ProcedureFeeValue,
  TableActionsPlaceholder,
  CdtCode,
  AppointmentTimeframeRow,
  AppointmentLoadingOverlay,
} from "./TreatmentAppointment.styled";
import AppointmentTimeSeparator from "../StepTimeSeparator";

interface TreatmentAppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
  isLastAppointment?: boolean;
  showTooltip?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  isDraggable?: boolean;
  isDeleting?: boolean;
  showActionsColumn?: boolean;
}

const TreatmentAppointment: React.FC<TreatmentAppointmentProps> = ({
  appointment,
  appointmentNumber,
  isLastAppointment = false,
  showTooltip = true,
  onEdit,
  onDelete,
  onDragStart,
  isDraggable = false,
  isDeleting = false,
  showActionsColumn = true,
}) => {
  const { t } = useTranslation();
  const appointmentNameRef = useRef<HTMLElement>(null);
  const [isAppointmentNameTruncated, setIsAppointmentNameTruncated] =
    useState(false);

  const renderCode = (
    code: string,
    count: number,
    key: string,
    tooltipText?: string,
  ) => {
    const content = (
      <CdtCode key={key} component="span">
        {code}
        {count > 1 && <Box component="span"> x{count}</Box>}
      </CdtCode>
    );
    if (!tooltipText || !showTooltip) return content;
    return (
      <Tooltip key={key} title={tooltipText} arrow placement="top">
        <span>{content}</span>
      </Tooltip>
    );
  };

  const formatCdtCodes = (
    entries: ReturnType<typeof deduplicateCdtCodesStructured>,
    descriptionsByCode: Record<string, string>,
  ): React.ReactNode => {
    if (entries.length === 0) return null;

    const hasMultipliers = entries.some((e) => e.count > 1);
    const perRow = hasMultipliers && entries.length <= 2 ? 1 : 2;

    const rows: (typeof entries)[] = [];
    for (let i = 0; i < entries.length; i += perRow) {
      rows.push(entries.slice(i, i + perRow));
    }

    return (
      <>
        {rows.map((row, rowIdx) => (
          <React.Fragment key={rowIdx}>
            {rowIdx > 0 && "\n"}
            {row.map((entry) => (
              <React.Fragment key={entry.code}>
                {renderCode(
                  entry.code,
                  entry.count,
                  entry.code,
                  descriptionsByCode[entry.code],
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </>
    );
  };

  const optionLabel = getOptionLabel(appointment.conditionalPath);
  const totalFee = getAppointmentFee(appointment);
  const duration = getAppointmentDuration(appointment);
  const appointmentName =
    appointment.description?.trim() ||
    t("treatmentPlan.appointment", { number: appointmentNumber });

  useEffect(() => {
    const element = appointmentNameRef.current;
    if (!element) {
      setIsAppointmentNameTruncated(false);
      return;
    }

    const checkTruncation = () => {
      setIsAppointmentNameTruncated(
        element.scrollHeight > element.clientHeight + 1,
      );
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [appointmentName]);

  const appointmentNameNode = (
    <Tooltip
      title={appointmentName}
      arrow
      placement="top"
      disableHoverListener={!isAppointmentNameTruncated}
    >
      {/* @ts-expect-error: styled Typography typing here doesn't include `component` prop. */}
      <AppointmentHeaderName ref={appointmentNameRef} component="span">
        {appointmentName}
      </AppointmentHeaderName>
    </Tooltip>
  );

  return (
    <AppointmentContainer isDeleting={isDeleting}>
      {isDeleting && (
        <AppointmentLoadingOverlay>
          <CircularProgress size={24} />
        </AppointmentLoadingOverlay>
      )}
      <AppointmentHeader showActionsColumn={showActionsColumn}>
        {isDraggable && onDragStart && (
          <DragHandle draggable onDragStart={onDragStart}>
            <MoveAppointmentIcon width="18px" height="18px" />
          </DragHandle>
        )}

        <AppointmentHeaderMainCell>
          <AppointmentHeaderTitleRow>
            <AppointmentHeaderNumber>
              #{appointmentNumber}
            </AppointmentHeaderNumber>
            {appointmentNameNode}
            {optionLabel && (
              <AppointmentHeaderBadge>
                {t("treatmentPlan.optionLabel", { label: optionLabel })}
              </AppointmentHeaderBadge>
            )}
            <AppointmentHeaderDuration>{duration}</AppointmentHeaderDuration>
          </AppointmentHeaderTitleRow>
        </AppointmentHeaderMainCell>

        <AppointmentHeaderFeeCell>
          <AppointmentFieldLabel>
            {t("treatmentPlan.fee")}
          </AppointmentFieldLabel>
          <AppointmentFee>${formatFeeWithoutCents(totalFee)}</AppointmentFee>
        </AppointmentHeaderFeeCell>

        {showActionsColumn && (
          <AppointmentActions>
            {onEdit && (
              <ActionIconButton
                size="small"
                onClick={onEdit}
                disabled={isDeleting}
              >
                <EditNoteIcon width="18px" height="18px" />
              </ActionIconButton>
            )}
            {onDelete && (
              <ActionIconButton
                size="small"
                onClick={onDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: colors.status.error }}
                  />
                ) : (
                  <TrashIcon
                    width="18px"
                    height="18px"
                    fill={colors.status.error}
                  />
                )}
              </ActionIconButton>
            )}
          </AppointmentActions>
        )}
      </AppointmentHeader>

      {[...appointment.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((step) => {
          const fee = getProcedureFee(step);
          const cdtEntries = deduplicateCdtCodesStructured(
            step.codeSuggestions,
          );
          const descriptionsByCode = step.codeSuggestions.reduce<
            Record<string, string>
          >((acc, item) => {
            if (item.code && item.description && !acc[item.code]) {
              acc[item.code] = item.description;
            }
            return acc;
          }, {});
          const cdtContent = formatCdtCodes(cdtEntries, descriptionsByCode);
          const { teeth, surfaces } = parseToothSurfaces(step.toothSurfaces);
          const hasCdtCode = step.codeSuggestions.length > 0;
          const treatAreaCapabilities = aggregateTreatAreaCapabilities(
            step.codeSuggestions,
          );

          return (
            <ProcedureCard key={step.id} showActionsColumn={showActionsColumn}>
              <ProcedureTitle>{step.name}</ProcedureTitle>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.cdtCode")}
                </ProcedureFieldLabel>
                {cdtContent ? (
                  <ProcedureCdtValue>{cdtContent}</ProcedureCdtValue>
                ) : (
                  <TreatmentStepEmptyValue>
                    {t("treatmentPlan.noCode")}
                  </TreatmentStepEmptyValue>
                )}
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.tooth")}
                </ProcedureFieldLabel>
                <TreatmentStepChips
                  values={teeth}
                  emptyLabel={t("treatmentPlan.noTooth")}
                  error={
                    hasCdtCode &&
                    hasMissingTooth(teeth, surfaces, treatAreaCapabilities)
                  }
                  errorReason={t("treatmentPlan.valueRequired")}
                />
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.surface")}
                </ProcedureFieldLabel>
                <TreatmentStepChips
                  values={surfaces}
                  emptyLabel={t("treatmentPlan.noSurface")}
                  error={
                    hasCdtCode &&
                    hasMissingSurface(teeth, surfaces, treatAreaCapabilities)
                  }
                  errorReason={t("treatmentPlan.valueRequired")}
                />
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.fee")}
                </ProcedureFieldLabel>
                <ProcedureFeeValue>
                  ${formatFeeWithoutCents(fee)}
                </ProcedureFeeValue>
              </ProcedureMobileField>
              {showActionsColumn && <TableActionsPlaceholder />}
            </ProcedureCard>
          );
        })}

      {!isLastAppointment && appointment.timeframeLabel && (
        <AppointmentTimeframeRow>
          <AppointmentTimeSeparator
            timeframeLabel={appointment.timeframeLabel}
          />
        </AppointmentTimeframeRow>
      )}
    </AppointmentContainer>
  );
};

export default TreatmentAppointment;
